import {
  Box,
  Divider,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Text,
  Toast
} from '@chakra-ui/react';
import {
  ButtonText,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Button,
  SearchInput,
  Badge
} from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { parseQueryParams, updateQueryParams } from 'src/utils/submissionsList';
import ListLoader from '../ListLoader';
import {
  adjudicationsDefaultValues,
  IAdjudicationsFilterStatus,
  IAdjudicationsListFilters,
  useAdjudicationsDashBoardConfig,
  useAdjudicationsList
} from 'src/pages/MyTask/Adjudications/hooks';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import {
  AdjudicationsTableHeader,
  AdjudicationsTableRow
} from './AdjudicationTable';
import { faBookSparkles } from '@fortawesome/pro-light-svg-icons';
import { useReviewerSavedFilters } from 'src/pages/ReviewList/hooks';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBarsFilter,
  faDeleteRight,
  faFloppyDisk,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import SaveFilterText from 'src/pages/Organization/Workspace/Submissions/SaveFilterText';
import { ISavedFilterSets } from '@publishing-engine/domain/models';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import SaveFiltersModal from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/SaveFilterModal';
import ManageSavedFiltersModal from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/ManageSavedFiltersModal';

const AdjudicationsListComponent = (props: {
  status: IAdjudicationsFilterStatus;
  label: string;
}) => {
  const { status } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const [sort, setSort] = useState<SortField | null>(null);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [showManageFiltersModal, setShowManageFiltersModal] =
    useState<boolean>(false);
  const { watch, reset, setValue, getValues } =
    useForm<IAdjudicationsListFilters>({
      defaultValues: adjudicationsDefaultValues
    });

  const { data: dashBoardConfigs } = useAdjudicationsDashBoardConfig(
    `adjudication-${status}`
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [searchValue, setSearchValue] = useState('');
  const { data: savedFilters, isLoading: isSavedFiltersLoading } =
    useReviewerSavedFilters(`adjudication-${status}`);
  const filters = watch();

  const {
    expertPhases,
    workflowPhase,
    searchParams,
    expertRecommendations,
    journalId
  } = filters;
  const hasFilters =
    !!expertPhases?.length ||
    !!workflowPhase?.length ||
    !!searchParams?.length ||
    !!journalId.length ||
    !!expertRecommendations?.length;

  const { data, isError, isLoading, page, setPage } = useAdjudicationsList(
    status,
    filters,
    dashBoardConfigs,
    sort ?? undefined,
    pageSize
  );
  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };
  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: keyof IAdjudicationsListFilters
  ) => {
    const currentFilter = getValues(fieldName) as string[];
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    );
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setFilterName(null);
  };

  const [showSaveFiltersModal, setShowSaveFiltersModal] =
    useState<boolean>(false);
  const offset = (page - 1) * pageSize;
  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, data?.total || 0);
  useEffect(() => {
    const { filters: newFilters } = parseQueryParams(location.search);
    // Update form state based on initial URL params
    reset(newFilters);
  }, [reset]);
  const [filterName, setFilterName] = useState<string | null>(null);
  const clearFilters = () => {
    reset(adjudicationsDefaultValues);
    setSearchValue('');
    setSelectedFilters({});
    navigate(location.pathname);
  };
  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as keyof IAdjudicationsListFilters, values as any);
    });
  };
  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };
  const handleSetValues = (data: ISavedFilterSets, name: string) => {
    setValue('searchParams', data?.searchParams ?? '');
    setValue('journalId', data?.journalId ?? []);
    setFilterName(name);
    setSearchValue(data?.searchParams ?? '');
    setValue('workflowPhase', data.workflowPhase ?? []);
    return Toast({
      variant: 'success',
      title: `${name} Filter Applied`
    });
  };
  // Sync URL query params to formState
  useEffect(() => {
    updateQueryParams(filters, location, navigate);
  }, [filters, navigate]);
  const handleSaveFiltersModal = (name: string | null) => {
    setShowSaveFiltersModal(prev => !prev);
    if (name) setFilterName(name);
  };
  const onDeleteClearFilters = () => {
    clearFilters();
    setFilterName(null);
  };
  return (
    <Flex direction={'column'} gap={4} width="100%">
      {showSaveFiltersModal ? (
        <SaveFiltersModal
          isOpen={showSaveFiltersModal}
          onClose={handleSaveFiltersModal}
          filter={filters}
          view={`adjudication-${status}`}
        />
      ) : null}

      {showManageFiltersModal ? (
        <ManageSavedFiltersModal
          isOpen={showManageFiltersModal}
          onClose={() => setShowManageFiltersModal(false)}
          savedFilters={savedFilters?.response}
          availableFilters={dashBoardConfigs?.filters}
          isSavedFiltersLoading={isSavedFiltersLoading}
          clearFilters={onDeleteClearFilters}
          view={`adjudication-${status}`}
        />
      ) : null}
      <Flex gap={2} flexDirection={'column'}>
        <Flex gap={4} mb={2}>
          <SearchInput
            placeholder="Search submissions by title or manuscript ID"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            width="100%"
            name="searchParams"
            value={searchValue}
            onChange={e => setSearchValue(e.currentTarget.value)}
            onInputClear={() => {
              setSearchValue('');
              setValue('searchParams', '');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault(); // Prevent form submission if inside a form
                setValue('searchParams', e.currentTarget.value.trim()); // Update the value in the form state
              }
            }}
          />
          {dashBoardConfigs?.filters && (
            <FilterDrawer
              filterGroups={dashBoardConfigs.filters.map((filter: any) => ({
                ...filter,
                items: filter.items ?? []
              }))}
              watch={watch}
              onSubmit={handleFiltersSubmit}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          )}
          <Flex>
            <Menu ariaLabel="filters">
              {({ onClose }) => (
                <>
                  <MenuButton
                    as={Button}
                    variant="secondary"
                    iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                  >
                    Saved Filters
                  </MenuButton>
                  <MenuList textStyle="headingItem">
                    {savedFilters?.response?.length ? (
                      <>
                        <Box maxH="300px" overflowY="auto" maxW="350px">
                          {savedFilters.response.map(item => (
                            <>
                              <MenuItem
                                onClick={() => {
                                  const filter =
                                    item.filters as ISavedFilterSets;
                                  handleSetValues(filter, item.name);
                                }}
                                ariaLabel={item.name}
                                key={item.id}
                              >
                                <Text textStyle="headingLine" isTruncated>
                                  {item.name}
                                </Text>
                                {
                                  <SaveFilterText
                                    saveFilters={
                                      item.filters as ISavedFilterSets
                                    }
                                    availableFilters={dashBoardConfigs?.filters}
                                    manageTags={[]}
                                  />
                                }
                              </MenuItem>
                              <MenuDivider />
                            </>
                          ))}
                        </Box>
                        <Flex
                          alignItems="center"
                          justifyContent="center"
                          padding={3}
                          position="sticky"
                        >
                          <ButtonText
                            onClick={() => {
                              setShowManageFiltersModal(true);
                              onClose();
                            }}
                          >
                            Manage Filters
                          </ButtonText>
                        </Flex>
                      </>
                    ) : (
                      <Flex
                        flexDirection={'column'}
                        gap={4}
                        padding={8}
                        justifyContent={'center'}
                        alignItems={'center'}
                        maxW="350px"
                      >
                        <Text textStyle="headingGroup">No Saved Filters</Text>
                        <Text textStyle="textBody" textAlign={'center'}>
                          You do not have any saved filters yet. Try applying
                          some filters and then click 'Save Filters' to save
                          them for later usage.
                        </Text>
                      </Flex>
                    )}
                  </MenuList>
                </>
              )}
            </Menu>
          </Flex>{' '}
        </Flex>
      </Flex>
      <Skeleton isLoaded={!isLoading}>
        <Flex
          alignItems={'center'}
          gap={4}
          height={5}
          justifyContent={'space-between'}
        >
          <Flex>
            <Text textStyle="textBody">
              {data && data?.total > 0 ? (
                <>
                  Showing <strong>{startIndex}</strong> –{' '}
                  <strong>{endIndex}</strong> of <strong>{data.total}</strong>{' '}
                  {status} Adjudication{data.total === 1 ? '' : 's'}.
                </>
              ) : (
                <>
                  <strong>0</strong> {status} Adjudication.
                </>
              )}
            </Text>
            {hasFilters && (
              <>
                {filterName ? (
                  <Box marginLeft={2}>
                    <Text textStyle="textBody">
                      <strong>{filterName}</strong> applied
                    </Text>
                  </Box>
                ) : (
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                    variant="secondary"
                    onClick={() => setShowSaveFiltersModal(true)}
                    marginX={2}
                  >
                    Save Filters
                  </ButtonText>
                )}
                <ButtonText
                  leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                  variant="secondary"
                  onClick={() => {
                    clearFilters();
                    setFilterName(null);
                  }}
                >
                  Clear Filters
                </ButtonText>
              </>
            )}
          </Flex>
          <Divider
            orientation="horizontal"
            flex="1"
            mx={4}
            borderColor="gray.300"
          />
          {dashBoardConfigs?.sortOptions?.fields && (
            <SortMenu
              appliedSort={sort}
              options={dashBoardConfigs.sortOptions}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          )}
        </Flex>
        {!!hasFilters && (
          <Flex alignItems="center" gap={2} wrap="wrap" marginTop={2}>
            {dashBoardConfigs?.filters?.map(filterConfig => {
              // Type guard to check if filterConfig has 'items'
              const hasItems = (fc: any): fc is { items: any[] } =>
                Array.isArray(fc.items);

              if (!hasItems(filterConfig) || filterConfig.items.length === 0) {
                return null;
              }

              const filterKey = filterConfig.key as keyof typeof filters;
              const filterValues = filters?.[filterKey];

              if (!Array.isArray(filterValues) || filterValues.length === 0) {
                return null;
              }

              return filterValues.map(value => {
                const item = filterConfig.items.find(
                  (i: any) => i.value === value
                );
                const label = item?.name ?? value;

                return (
                  <Badge
                    key={`${filterKey}-${value}`}
                    size="base"
                    variant="interactive"
                  >
                    <Flex alignItems="center" gap={2}>
                      {label}
                      <FontAwesomeIcon
                        icon={faXmark}
                        cursor="pointer"
                        onClick={() =>
                          removeSingleFilter(
                            value,
                            filterKey as keyof IAdjudicationsListFilters
                          )
                        }
                      />
                    </Flex>
                  </Badge>
                );
              });
            })}
          </Flex>
        )}
      </Skeleton>
      <Box width="100%">
        {isLoading ? (
          <ListLoader />
        ) : data?.total ? (
          <>
            <Box mx={-16}>
              <Box px={16}>
                <Flex flexDirection={'column'}>
                  {/* @ts-ignore */}
                  <Table width="1010px" minWidth="100%">
                    <AdjudicationsTableHeader />
                    <Tbody>
                      {data.items.map(data => (
                        <AdjudicationsTableRow data={data} />
                      ))}
                    </Tbody>
                  </Table>
                </Flex>
              </Box>
            </Box>
            <TablePagination
              ariaLabel="Adjudications pagination"
              currentPage={page}
              totalItems={data.total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        ) : isError ? (
          <SubmissionLoader
            title="Search Failed"
            subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
            bgColor="errorPrimaryFill"
          />
        ) : (
          <SubmissionLoader
            title={`No Adjudications Found`}
            icon={faBookSparkles}
            subTitle={
              'Adjudications will appear here after you accept invitations and start evaluating reviews.'
            }
            bgColor="neutralSecondaryFill"
          />
        )}
      </Box>
    </Flex>
  );
};

export default AdjudicationsListComponent;
